// @file This store manages screen recording in a more minimal sense compared to surface_screen_recorder_store.ts which it is based on
/**
 * This store is used to handle the case where a user is recording their screen for a single post (no draft/comment handling)
 * note: this store assumes there will only be one screen recording session at a time for a user
 */

import { ScreenRecorderStatus } from '@@/bits/screen_recorder'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

export type SaveRecordingHandler = (
  file: File,
  options?: { trimVideoAfterUpload?: { start: number; end: number } },
) => void

export const useScreenRecorderStore = defineStore('screenRecorderStore', () => {
  // #region: visibility for screen recorder modal
  const isVisible = ref(false)
  const saveRecordingHandler = ref<SaveRecordingHandler | null>(null)

  const isRecording = computed(
    () => status.value === ScreenRecorderStatus.Recording || status.value === ScreenRecorderStatus.Paused,
  )

  /**
   * Register a save handler to be called when recording completes.
   * Call this before the user opens the screen recorder (e.g., when opening ContentPicker).
   */
  const registerSaveHandler = (handler: SaveRecordingHandler): void => {
    saveRecordingHandler.value = handler
  }

  const showScreenRecorder = (handler?: SaveRecordingHandler): void => {
    // Use provided handler, or keep the pre-registered one
    if (handler) {
      saveRecordingHandler.value = handler
    }
    isVisible.value = true
    status.value = ScreenRecorderStatus.Connecting
  }

  const hideScreenRecorder = (): void => {
    isVisible.value = false
    status.value = ScreenRecorderStatus.Hidden
    saveRecordingHandler.value = null
    returnToUrl.value = null
  }

  /**
   * Handle saving a recording by calling the registered handler.
   * This should be called by the ScreenRecorder component's @handle-save-recording event.
   */
  const handleSaveRecording = (
    file: File,
    options?: { trimVideoAfterUpload?: { start: number; end: number } },
  ): void => {
    if (saveRecordingHandler.value) {
      saveRecordingHandler.value(file, options)
    }
    hideScreenRecorder()
  }
  // #endregion

  // #region status for screen recorder modal
  const status = ref<ScreenRecorderStatus>(ScreenRecorderStatus.Hidden)
  const setStatus = (newStatus: ScreenRecorderStatus): void => {
    status.value = newStatus
  }

  // Navigate back to original URL when user stops recording (enters preview mode)
  // This ensures the component is mounted before save, so the handler works correctly
  watch(status, (newStatus) => {
    if (newStatus === ScreenRecorderStatus.Previewing && returnToUrl.value) {
      if (window.location.href !== returnToUrl.value) {
        window.history.pushState(null, '', returnToUrl.value)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
      // Clear returnToUrl since we've navigated back
      returnToUrl.value = null
    }
  })
  // #endregion

  // #region: z index override for screen recorder modal
  const zIndexOverride = ref<number | null>(null)
  const setZIndexOverride = (zIndex: number | null): void => {
    zIndexOverride.value = zIndex
  }
  // #endregion

  // #region: return-to-URL for navigation during recording
  /**
   * URL to navigate back to when recording stops (enters preview mode).
   * Set this when starting a recording from a specific context (e.g., discussion message input).
   */
  const returnToUrl = ref<string | null>(null)
  const setReturnToUrl = (url: string | null): void => {
    returnToUrl.value = url
  }
  // #endregion

  return {
    // visibility
    isVisible,
    isRecording,
    zIndexOverride,
    setZIndexOverride,
    registerSaveHandler,
    showScreenRecorder,
    hideScreenRecorder,
    handleSaveRecording,

    // status
    status,
    setStatus,

    // return-to-URL
    returnToUrl,
    setReturnToUrl,
  }
})
